import React, { useState, useEffect, useRef } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './ConversationPanel.module.css';
import assistantStyles from '../AssistantView/AssistantView.module.css';
import classNames from 'classnames';
import type {
  AssistantMessage,
  AssistantChat,
} from 'components/framework/models';
import ShareChatPopover from '../SharePopover/ShareChatPopover';
import ThinkingIcon from '../ThinkingIcon/ThinkingIcon';
import {
  flattenMessagesForRendering,
  groupItemsForDisplay,
  renderMessageGroups,
} from './messageProcessingUtils';

const EditableChatName = ApplicationView(
  ({
    chat,
    initialEditMode,
  }: {
    chat: AssistantChat;
    initialEditMode?: boolean;
  }) => {
    const controller = useController<AssistantViewController>();
    const [editMode, setEditMode] = useState(initialEditMode || false);
    const [value, setValue] = useState(chat.name);

    const save = () => {
      chat.name = value;
      void chat.save();
      setEditMode(false);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        save();
      } else if (e.key === 'Escape') {
        setValue(chat.name);
        setEditMode(false);
      }
    };
    useEffect(() => {
      setValue(chat.name);
    }, [chat.name]);

    // Handle initialEditMode changes
    useEffect(() => {
      if (initialEditMode) {
        setEditMode(true);
        // Clear the edit mode flag in the controller after setting local state
        controller.actionClearEditNameMode();
      }
    }, [initialEditMode, controller]);

    return (
      <div className={styles.chatName}>
        {editMode ? (
          <input
            type='text'
            autoFocus
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => handleKeyDown(e)}
            onBlur={() => save()}
          />
        ) : (
          <span onClick={() => setEditMode(true)}>
            {value} <i className='far fa-pencil' />
          </span>
        )}
      </div>
    );
  }
);

const ConversationToolbar = ApplicationView(() => {
  const controller = useController<AssistantViewController>();
  const { model, readOnly, promptPrefix, size, editNameMode } =
    controller.state;
  const chat = model.currentChat;
  const isDevelopment = ['development', 'staging'].includes(
    process.env.NODE_ENV || 'unknown'
  );

  if (promptPrefix) return null; // Don't show toolbar when prefix is set.
  if (size === 'toolbar') return null;

  if (readOnly) {
    return (
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.chatName}>{chat.name}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        <EditableChatName chat={chat} initialEditMode={editNameMode} />
      </div>
      <div className={styles.toolbarRight}>
        {isDevelopment && (
          <>
            <aha-button
              kind='plain'
              size='mini'
              onClick={() =>
                window.open(`/dev/assistant_chats/${chat.id}`, '_blank')
              }
            >
              <i className='far fa-tools' />
            </aha-button>
            <aha-tooltip>View in Dev Tools</aha-tooltip>
          </>
        )}
        <ShareChatPopover chat={chat} />
      </div>
    </div>
  );
});

// Component to size and scroll the conversation panel. The scrolling philosophy is:
//
// * When a new user message is added we scroll that to the top of the window.
// * As the new assistant content is added we don't scroll at all so that the user
//  can read the content, and scroll as necessary.
// * We use artificial margin to make the conversation big enough to allow the
//   scrolling to happen.
//
// This component does some funky imperative DOM manipulation to make this work.
// Note that it doesn't play well with CSS transitions since it measures the DOM
// at the start of the transition, not the end.
const Pushdown = ApplicationView(() => {
  const ref = useRef<HTMLImageElement>(null);
  const controller = useController<AssistantViewController>();
  const { model, layoutData, size } = controller.state;
  const [marginBottom, setMarginBottom] = React.useState(0);
  const [lastScrolledMessage, setLastScrolledMessage] = React.useState<
    AssistantMessage | undefined
  >();
  const lastUserMessage = model.lastMessage('user');
  const content = model.lastMessage('assistant')?.content; // Force re-render when the last message changes.

  useEffect(() => {
    if (ref.current) {
      // Make sure the conversation is at least tall enough to fill the parent.
      const container = ref.current.closest(
        `.${assistantStyles.conversationColumn}`
      );
      if (container) {
        const parentHeight = container.clientHeight;

        const visibleHeight = ref.current.offsetTop - layoutData.topOfMessage;
        const bottomMargin = parentHeight - visibleHeight;

        setMarginBottom(Math.max(0, bottomMargin - 80));
      }
    }
  }, [size, layoutData.topOfMessage, content]);

  useEffect(() => {
    if (
      ref.current &&
      ref.current.style.marginBottom === `${marginBottom}px` &&
      lastUserMessage !== lastScrolledMessage
    ) {
      const container = ref.current.closest(
        `.${assistantStyles.conversationColumn}`
      );
      if (container) {
        // 40 to leave space for the toolbar.
        container.scrollTop = layoutData.topOfMessage - 10 - 40;
        setLastScrolledMessage(lastUserMessage);
      }
    }
  }, [
    lastScrolledMessage,
    lastUserMessage,
    layoutData.topOfMessage,
    marginBottom,
  ]);

  return <div ref={ref} className={styles.pushdown} style={{ marginBottom }} />;
});

const ConversationPanel: React.FC = () => {
  const controller = useController<AssistantViewController>();
  const conversationRef = useRef<HTMLDivElement>(null);
  const { model, readOnly } = controller.state;
  const chat = model.currentChat;

  const flattenedItems = flattenMessagesForRendering(chat.messages || []);
  const groupedItems = groupItemsForDisplay(flattenedItems);
  const messages = renderMessageGroups(controller, groupedItems, readOnly);

  const lastMessage = chat.messages[chat.messages.length - 1];
  const lastToolCall =
    lastMessage?.toolCalls?.[lastMessage.toolCalls.length - 1];
  const lastMessageActive =
    lastMessage &&
    lastMessage.role === 'assistant' &&
    (lastMessage.active ||
      (lastToolCall &&
        ['preparing', 'processing'].includes(lastToolCall.status)));

  if (model.loading) {
    return (
      <div className={classNames(styles.conversation, styles.loading)}>
        <aha-spinner />
      </div>
    );
  }

  return (
    <>
      <ConversationToolbar />
      <div
        className={styles.conversation}
        ref={conversationRef}
        data-chat-id={chat.id}
      >
        {messages}
        {model.active && !lastMessageActive && (
          <div className={styles.activeIndicator}>
            <ThinkingIcon /> Thinking
          </div>
        )}
        {!model.active && !lastMessageActive && (
          <span className={styles.reviewResultsCarefully}>
            Please carefully review the results. AI sometimes makes mistakes.
          </span>
        )}
        {!readOnly && <Pushdown />}
      </div>
    </>
  );
};

export default ApplicationView(ConversationPanel);
